import React from 'react';
import {
  Square3Stack3DIcon,
  BoltIcon,
  ClockIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline';

interface KpiSummaryBarProps {
  totalMatters: number;
  activeMatters: number;
  pendingMatters: number;
  recentlyClosed: number;
}

const KpiSummaryBar: React.FC<KpiSummaryBarProps> = ({
  totalMatters,
  activeMatters,
  pendingMatters,
  recentlyClosed,
}) => {
  const kpis = [
    {
      label: 'Total Engagements',
      value: totalMatters,
      Icon: Square3Stack3DIcon,
      iconColor: 'text-lex-accent',
      valueColor: 'text-lex-accent',
      bgGradient: 'bg-lex-accent-soft',
      borderColor: 'border-lex-accent',
      glowColor: 'hover:shadow-lex-accent/20',
    },
    {
      label: 'Active Engagements',
      value: activeMatters,
      Icon: BoltIcon,
      iconColor: 'text-emerald-500 dark:text-emerald-400',
      valueColor: 'text-emerald-600 dark:text-emerald-400',
      bgGradient: 'bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-900/30 dark:to-emerald-800/20',
      borderColor: 'border-emerald-200/50 dark:border-emerald-700/30',
      glowColor: 'hover:shadow-emerald-500/20',
      pulse: true, // Active items get a subtle pulse
    },
    {
      label: 'Pending Engagements',
      value: pendingMatters,
      Icon: ClockIcon,
      iconColor: 'text-amber-500 dark:text-amber-400',
      valueColor: 'text-amber-600 dark:text-amber-400',
      bgGradient: 'bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-900/30 dark:to-amber-800/20',
      borderColor: 'border-amber-200/50 dark:border-amber-700/30',
      glowColor: 'hover:shadow-amber-500/20',
    },
    {
      label: 'Recently Closed',
      value: recentlyClosed,
      Icon: CheckBadgeIcon,
      iconColor: 'text-slate-500 dark:text-slate-400',
      valueColor: 'text-slate-600 dark:text-slate-400',
      bgGradient: 'bg-gradient-to-br from-slate-50 to-slate-100/50 dark:from-slate-900/30 dark:to-slate-800/20',
      borderColor: 'border-slate-200/50 dark:border-slate-700/30',
      glowColor: 'hover:shadow-slate-500/20',
    },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi, index) => (
          <div
            key={index}
            className={`
              ${kpi.bgGradient}
              ${kpi.borderColor}
              ${kpi.glowColor}
              rounded-xl p-4
              border
              transition-all duration-300 ease-out
              hover:scale-[1.02] hover:shadow-lg
              cursor-default
              group
            `}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                  {kpi.label}
                </p>
                <p className={`text-3xl font-bold ${kpi.valueColor} tabular-nums`}>
                  {kpi.value.toLocaleString()}
                </p>
              </div>
              <div className={`
                ${kpi.iconColor}
                p-2.5 rounded-lg
                bg-white/60 dark:bg-gray-800/60
                backdrop-blur-sm
                transition-transform duration-300
                group-hover:scale-110 group-hover:rotate-3
                ${kpi.pulse ? 'animate-pulse' : ''}
              `}>
                <kpi.Icon className="h-7 w-7" strokeWidth={1.5} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default KpiSummaryBar;
